import { z } from 'zod'
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js/max'
import { REQUIRED_MESSAGE } from '@Const/error-messages'
import { FormFieldTypes } from '@App/enum/form-field-types'
import type { InternationalPhoneInputProps } from '@Fields/v2/InternationalPhoneInput'

export const phoneCountryCodeKey = 'phoneCountryCode'
export const fullPhoneNumberKey = 'fullPhoneNumber'
export const internationalPhoneNumberFormKey = 'phoneNumber'

const INVALID_PHONE_MESSAGE = 'Please enter a valid phone number'

export const internationalPhoneNumberSchema = z
    .object({
        [phoneCountryCodeKey]: z.string().min(1, REQUIRED_MESSAGE),
        [fullPhoneNumberKey]: z.string({ error: REQUIRED_MESSAGE }).min(1, REQUIRED_MESSAGE),
    })
    .refine(
        ({ phoneCountryCode, fullPhoneNumber }) =>
            !phoneCountryCode || !fullPhoneNumber || isValidPhoneNumber(`+${phoneCountryCode}${fullPhoneNumber}`),
        { message: INVALID_PHONE_MESSAGE, path: [fullPhoneNumberKey] }
    )

export type InternationalPhoneNumberType = z.infer<typeof internationalPhoneNumberSchema>

/**
 * The phone input works with a single string meanwhile the backend and the form want the country code and the rest of the phone number split
 * AsYouType resolves the calling code from libphonenumber metadata to match the backend
 */
export const splitPhoneNumber = (value: string | undefined): InternationalPhoneNumberType => {
    const digits = (value ?? '').replace(/\D/g, '')

    if (!digits) {
        return { [phoneCountryCodeKey]: '', [fullPhoneNumberKey]: '' }
    }

    const asYouType = new AsYouType()
    asYouType.input(value ?? '')
    const callingCode = asYouType.getCallingCode() ?? ''

    return {
        [phoneCountryCodeKey]: callingCode,
        [fullPhoneNumberKey]: digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits,
    }
}

export const joinPhoneNumber = (phoneNumber: Partial<InternationalPhoneNumberType> | undefined) => {
    const countryCode = phoneNumber?.[phoneCountryCodeKey]

    return countryCode ? `+${countryCode}${phoneNumber?.[fullPhoneNumberKey] ?? ''}` : ''
}

export const internationalPhoneNumberFormField: InternationalPhoneInputProps = {
    name: internationalPhoneNumberFormKey,
    label: 'Phone Number',
    type: FormFieldTypes.INTERNATIONAL_PHONE,
}
